// Services Supabase pour le système de formation
import { supabase } from './supabaseClient';

// =====================================================
// TYPES
// =====================================================

export interface TrainingCategory {
    id: number;
    name: string;
    description: string | null;
    color: string;
    icon: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface Training {
    id: number;
    category_id: number;
    title: string;
    description: string;
    short_description: string | null;
    youtube_url: string;
    youtube_video_id: string | null;
    thumbnail_url: string | null;
    duration_minutes: number | null;
    difficulty_level: 'beginner' | 'intermediate' | 'advanced';
    tags: string[] | null;
    prerequisites: string[] | null;
    learning_objectives: string[] | null;
    target_audience: string | null;
    view_count: number;
    like_count: number;
    status: 'draft' | 'published' | 'archived';
    featured: boolean;
    instructor_name: string | null;
    instructor_bio: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
    // Relations
    category?: TrainingCategory;
}

export interface TrainingView {
    id: number;
    training_id: number;
    member_id: string | null;
    watch_duration_seconds: number;
    completed: boolean;
    created_at: string;
}

export interface TrainingReaction {
    id: number;
    training_id: number;
    member_id: string;
    reaction_type: 'like' | 'dislike';
    created_at: string;
}

export interface TrainingComment {
    id: number;
    training_id: number;
    member_id: string;
    parent_comment_id: number | null;
    comment: string;
    is_approved: boolean;
    like_count: number;
    created_at: string;
    updated_at: string;
    // Relations
    member?: {
        first_name: string;
        last_name: string;
        selfie_url: string | null;
    };
    replies?: TrainingComment[];
}

// =====================================================
// SERVICES CATEGORIES
// =====================================================

export const trainingCategoryService = {
    async getAll(): Promise<TrainingCategory[]> {
        const { data, error } = await supabase
            .from('training_categories')
            .select('*')
            .eq('is_active', true)
            .order('name');

        if (error) throw error;
        return data || [];
    },

    async getById(id: number): Promise<TrainingCategory | null> {
        const { data, error } = await supabase
            .from('training_categories')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }
        return data;
    }
};

// =====================================================
// SERVICES FORMATIONS
// =====================================================

export const trainingService = {
    // Recuperer toutes les formations publiees
    async getPublished(options?: {
        categoryId?: number;
        featured?: boolean;
        limit?: number;
        offset?: number;
        search?: string;
    }): Promise<{ trainings: Training[]; total: number }> {
        let query = supabase
            .from('trainings')
            .select(`
                *,
                category:training_categories(id, name, color, icon)
            `, { count: 'exact' })
            .eq('status', 'published');

        if (options?.categoryId) {
            query = query.eq('category_id', options.categoryId);
        }

        if (options?.featured !== undefined) {
            query = query.eq('featured', options.featured);
        }

        if (options?.search) {
            query = query.or(`title.ilike.%${options.search}%,description.ilike.%${options.search}%`);
        }

        query = query.order('featured', { ascending: false })
            .order('created_at', { ascending: false });

        if (options?.limit) {
            query = query.limit(options.limit);
        }

        if (options?.offset) {
            query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
        }

        const { data, error, count } = await query;

        if (error) throw error;
        return { trainings: data || [], total: count || 0 };
    },

    // Recuperer les formations vedettes
    async getFeatured(limit: number = 4): Promise<Training[]> {
        const { data, error } = await supabase
            .from('trainings')
            .select(`
                *,
                category:training_categories(id, name, color, icon)
            `)
            .eq('status', 'published')
            .eq('featured', true)
            .order('view_count', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data || [];
    },

    // Recuperer une formation par ID
    async getById(id: number): Promise<Training | null> {
        const { data, error } = await supabase
            .from('trainings')
            .select(`
                *,
                category:training_categories(id, name, color, icon)
            `)
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            throw error;
        }
        return data;
    },

    // Enregistrer une vue
    async recordView(trainingId: number, memberId?: string): Promise<void> {
        // Incrementer le compteur de vues
        await supabase.rpc('increment_training_views', { training_id: trainingId });

        // Enregistrer la vue detaillee
        await supabase
            .from('training_views')
            .insert({
                training_id: trainingId,
                member_id: memberId || null
            });
    },

    // Mettre a jour la duree de visionnage
    async updateWatchDuration(viewId: number, durationSeconds: number, completed: boolean): Promise<void> {
        const { error } = await supabase
            .from('training_views')
            .update({
                watch_duration_seconds: durationSeconds,
                completed
            })
            .eq('id', viewId);

        if (error) throw error;
    },

    // Verifier si le membre a complete la formation
    async hasCompleted(trainingId: number, memberId: string): Promise<boolean> {
        const { count } = await supabase
            .from('training_views')
            .select('id', { count: 'exact' })
            .eq('training_id', trainingId)
            .eq('member_id', memberId)
            .eq('completed', true);

        return (count || 0) > 0;
    },

    // Recuperer les formations completees par un membre
    async getCompletedByMember(memberId: string): Promise<Training[]> {
        const { data, error } = await supabase
            .from('training_views')
            .select(`
                training:trainings(
                    *,
                    category:training_categories(id, name, color, icon)
                )
            `)
            .eq('member_id', memberId)
            .eq('completed', true);

        if (error) throw error;
        return (data?.map(d => d.training).filter(Boolean) || []) as unknown as Training[];
    }
};

// =====================================================
// SERVICES REACTIONS
// =====================================================

export const trainingReactionService = {
    // Ajouter ou modifier une reaction
    async toggle(trainingId: number, memberId: string, reactionType: 'like' | 'dislike'): Promise<void> {
        // Verifier si une reaction existe deja
        const { data: existing } = await supabase
            .from('training_reactions')
            .select('id, reaction_type')
            .eq('training_id', trainingId)
            .eq('member_id', memberId)
            .single();

        if (existing) {
            if (existing.reaction_type === reactionType) {
                // Supprimer la reaction si meme type
                await supabase
                    .from('training_reactions')
                    .delete()
                    .eq('id', existing.id);
            } else {
                // Changer le type de reaction
                await supabase
                    .from('training_reactions')
                    .update({ reaction_type: reactionType })
                    .eq('id', existing.id);
            }
        } else {
            // Creer une nouvelle reaction
            await supabase
                .from('training_reactions')
                .insert({
                    training_id: trainingId,
                    member_id: memberId,
                    reaction_type: reactionType
                });
        }
    },

    // Recuperer la reaction d'un membre
    async getMemberReaction(trainingId: number, memberId: string): Promise<'like' | 'dislike' | null> {
        const { data } = await supabase
            .from('training_reactions')
            .select('reaction_type')
            .eq('training_id', trainingId)
            .eq('member_id', memberId)
            .single();

        return data?.reaction_type || null;
    }
};

// =====================================================
// SERVICES COMMENTAIRES
// =====================================================

export const trainingCommentService = {
    // Recuperer les commentaires d'une formation
    async getByTraining(trainingId: number): Promise<TrainingComment[]> {
        const { data, error } = await supabase
            .from('training_comments')
            .select(`
                *,
                member:members(first_name, last_name, selfie_url)
            `)
            .eq('training_id', trainingId)
            .eq('is_approved', true)
            .is('parent_comment_id', null)
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Recuperer les reponses
        if (data && data.length > 0) {
            const commentIds = data.map(c => c.id);
            const { data: replies } = await supabase
                .from('training_comments')
                .select(`
                    *,
                    member:members(first_name, last_name, selfie_url)
                `)
                .in('parent_comment_id', commentIds)
                .eq('is_approved', true)
                .order('created_at', { ascending: true });

            // Attacher les reponses aux commentaires parents
            return data.map(comment => ({
                ...comment,
                replies: replies?.filter(r => r.parent_comment_id === comment.id) || []
            }));
        }

        return data || [];
    },

    // Ajouter un commentaire
    async add(trainingId: number, memberId: string, comment: string, parentCommentId?: number): Promise<TrainingComment> {
        const { data, error } = await supabase
            .from('training_comments')
            .insert({
                training_id: trainingId,
                member_id: memberId,
                comment,
                parent_comment_id: parentCommentId || null
            })
            .select(`
                *,
                member:members(first_name, last_name, selfie_url)
            `)
            .single();

        if (error) throw error;
        return data;
    },

    // Liker un commentaire
    async toggleLike(commentId: number, memberId: string): Promise<void> {
        const { data: existing } = await supabase
            .from('training_comment_likes')
            .select('id')
            .eq('comment_id', commentId)
            .eq('member_id', memberId)
            .single();

        if (existing) {
            await supabase
                .from('training_comment_likes')
                .delete()
                .eq('id', existing.id);
        } else {
            await supabase
                .from('training_comment_likes')
                .insert({
                    comment_id: commentId,
                    member_id: memberId
                });
        }
    }
};

// =====================================================
// STATISTIQUES
// =====================================================

export const trainingStatsService = {
    // Statistiques globales pour un membre
    async getMemberStats(memberId: string): Promise<{
        totalCompleted: number;
        totalHoursWatched: number;
        certificatesEarned: number;
    }> {
        const { data: views } = await supabase
            .from('training_views')
            .select('watch_duration_seconds, completed')
            .eq('member_id', memberId);

        const totalCompleted = views?.filter(v => v.completed).length || 0;
        const totalSecondsWatched = views?.reduce((sum, v) => sum + (v.watch_duration_seconds || 0), 0) || 0;

        return {
            totalCompleted,
            totalHoursWatched: Math.round(totalSecondsWatched / 3600),
            certificatesEarned: totalCompleted // Pour l'instant, 1 certificat par formation completee
        };
    },

    // Statistiques globales
    async getGlobalStats(): Promise<{
        totalTrainings: number;
        totalViews: number;
        totalHours: number;
    }> {
        const { count: totalTrainings } = await supabase
            .from('trainings')
            .select('id', { count: 'exact' })
            .eq('status', 'published');

        const { data: trainings } = await supabase
            .from('trainings')
            .select('view_count, duration_minutes')
            .eq('status', 'published');

        const totalViews = trainings?.reduce((sum, t) => sum + (t.view_count || 0), 0) || 0;
        const totalMinutes = trainings?.reduce((sum, t) => sum + (t.duration_minutes || 0), 0) || 0;

        return {
            totalTrainings: totalTrainings || 0,
            totalViews,
            totalHours: Math.round(totalMinutes / 60)
        };
    }
};
